import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getBanners } from 'legacyfrontend/redux/actions/banners-actions';
import { selectBannersState } from 'legacyfrontend/redux/selectors/application-selectors';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { RESET_BANNERS_ERROR } from 'legacyfrontend/redux/actions/action-types';
import BannerHoverx4 from './bannerComponents/BannerHoverx4';
import BannerSimpleX3 from './bannerComponents/BannerSimpleX3';
import Carousel3 from './bannerComponents/Carousel3';
import CarouselBanners from './bannerComponents/CarouselBanners';
import FeaturedSeventyFiveTwentyFive from './bannerComponents/FeaturedSeventyFiveTwentyFive';
import Featuredx2 from './bannerComponents/Featuredx2';
import FullWidthBanner from './bannerComponents/FullWidthBanner';
import OffersGalery from './bannerComponents/OffersGalery';
import VideoBanner from './bannerComponents/video';
import { LoadingBannersState } from './components/skeletons/BannerSkeletons';
import ContenidoBlock from '../../../frontend/features/contenido/ContenidoBlock';

function getComponentByUbication(component, ubicacionObj, componentIndex, ubicacionIndex) {
  const componentsMap = {
    'destacado-2-lugares-75-25': FeaturedSeventyFiveTwentyFive,
    '2-banner-ancho-total': Featuredx2,
    'galeria-ofertas': OffersGalery,
    'banner-zocalo-x3': BannerSimpleX3,
    'carrusel-4-elementos': CarouselBanners,
    'carrusel-3-elementos': Carousel3,
    video: VideoBanner,
    'banner-simple-ancho-total': FullWidthBanner,
    'banner-hover-x4': BannerHoverx4,
  };

  const categoryName = component.categoria.nombre;
  const ubicationName = ubicacionObj?.nombre;
  const TheComponent = componentsMap[ubicationName];

  return (
    TheComponent !== undefined && (
      <TheComponent
        key={`${ubicationName}-${categoryName}-${component.banner[0].id}-${ubicacionObj.id
          }-${componentIndex}${ubicacionIndex !== undefined ? `-${ubicacionIndex}` : ''}`}
        data={component}
      />
    )
  );
}

function mapComponent(component, index) {
  if (component.categoria?.ubicacion) {
    const result = Array.isArray(component.categoria.ubicacion)
      ? component.categoria.ubicacion.map((ubicacion, ubicacionIndex) =>
        getComponentByUbication(component, ubicacion, index, ubicacionIndex)
      )
      : getComponentByUbication(component, component.categoria.ubicacion, index);

    return result;
  }
  return null;
}

const ErrorBannersState = ({ onRetry, isLoading = false }) => {
  const { background } = useColors();

  return (
    <div className='flex flex-col items-center justify-center rounded px-10 py-16 text-center'>
      <div className='text-red-500 mb-4'>
        <svg className='w-12 h-12 mx-auto' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z'
          />
        </svg>
      </div>
      <h3 className='text-lg font-medium text-gray-700 mb-2'>Error al cargar contenido</h3>
      <p className='text-sm text-gray-500 mb-4'>No pudimos cargar los banners en este momento</p>
      {onRetry && (
        <button
          onClick={onRetry}
          disabled={isLoading}
          className='px-4 py-2 text-white rounded hover:opacity-90 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2'
          style={{
            backgroundColor: background,
          }}
        >
          {isLoading && (
            <svg className='animate-spin h-4 w-4' fill='none' viewBox='0 0 24 24'>
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          )}
          {isLoading ? 'Cargando...' : 'Intentar nuevamente'}
        </button>
      )}
    </div>
  );
};

const EmptyBannersState = () => (
  <div className='flex flex-col items-center justify-center rounded px-10 py-16 text-center'>
    <div className='text-gray-400 mb-4'>
      <svg className='w-12 h-12 mx-auto' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
        />
      </svg>
    </div>
    <h3 className='text-lg font-medium text-gray-700 mb-2'>No hay contenido disponible</h3>
    <p className='text-sm text-gray-500'>En este momento no tenemos banners para mostrar</p>
  </div>
);

export default function BannersComponents() {
  const { ready, error, categories } = useSelector(selectBannersState);
  const dispatch = useDispatch();
  const [isRetrying, setIsRetrying] = React.useState(false);

  useEffect(() => {
    if (!ready && !error) {
      dispatch(getBanners({ esDefault: true, showHomeOnly: true }));
    }
  }, [ready, error, dispatch]);

  useEffect(() => {
    if (ready && !error) {
      setIsRetrying(false);
    } else if (error) {
      setIsRetrying(false);
    }
  }, [ready, error]);

  const handleRetry = () => {
    setIsRetrying(true);
    dispatch({ type: RESET_BANNERS_ERROR });
    dispatch(getBanners({ esDefault: true, showHomeOnly: true }));
  };

  const renderComponents = useMemo(() => {
    if (ready && !error && categories) {
      let theComponents = [];
      const validCategories = categories.filter(
        (component) =>
          component.contenido || (component.banner && component.banner.length > 0)
      );

      if (validCategories.length === 0) {
        return null;
      }

      validCategories.forEach((component, index) => {
        if (component.contenido) {
          theComponents = theComponents.concat(
            <ContenidoBlock
              key={`contenido-${component.contenido.id}`}
              templateId={component.contenido.templateId}
              datos={component.contenido.datos || {}}
              acento={component.contenido.acento || '#f1c050'}
              animaciones={component.contenido.animaciones !== false}
              mode="live"
            />
          );
          return;
        }
        theComponents = theComponents.concat(mapComponent(component, index));
      });
      return theComponents;
    }
    return [];
  }, [ready, error, categories]);

  return (
    <div className='pt-10 pb-10 px-2 md:px-10'>
      {!ready && !error && <LoadingBannersState />}
      {ready && error && <ErrorBannersState onRetry={handleRetry} isLoading={isRetrying} />}
      {ready && !error && renderComponents === null && <EmptyBannersState />}
      {ready && !error && renderComponents && renderComponents.length > 0 && renderComponents}
    </div>
  );
}
